import { Box, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { userIsReadOnly } from "../../auth/AuthService";
import { HttpRequestError } from "../../auth/HttpInterceptor";
import DotLegalSkeletonTable from "../../common/table/DotLegalTableSkeleton";
import CustomExcelExport from "../csvExport/CustomExcelExport";
import ExcelExport from "../csvExport/ExcelExport";
import { generateNoDataDisplay, generateServiceUnavailableDisplay } from "../dashboard/DashboardWidgetService";
import { ApplicationUser } from "../users/User.types";
import { BulkRenewalAction, getBulkSelectability, getHeaderCheckBoxState } from "./bulkRenewal/bulkRenewal.functions";
import BulkRenewalBanner from "./bulkRenewal/BulkRenewalBanner";
import { BulkRenewalContext, BulkRenewalSelection } from "./bulkRenewal/BulkRenewalContext";
import BulkRenewalHeaderMenu from "./bulkRenewal/BulkRenewalHeaderMenu";
import BulkRenewalModal from "./bulkRenewal/BulkRenewalModal";
import { applyUpdatedCaseToRow } from "./Case.functions";
import useCasePageStyles from "./Case.styles";
import { Case, CaseFilter, CasesResponse, CaseTableArrangement, CaseType, RenewalStatus } from "./Case.types";
import CaseModalContent from "./CaseModalContent";
import { getCases, getRenewals, initialCaseTable, initialCaseTableArrangement, initialCaseTableHeaders, saveRenewalPrice } from "./CaseService";
import { CaseSummarization } from "./CaseSummarizationPage";
import CaseTable from "./CaseTable";
import getCaseTableHeaders from "./CaseTableHeaderService";
import RenewalRemarkModal from "./RenewalRemarkModal";

export type CaseTableWrapperProps = {
    caseFilter: CaseFilter;
    caseSummarization?: CaseSummarization;
    downloadExcel: () => void;
    isLoadingExcel: boolean;
    onCaseFilterChanged?: (caseType: CaseType, filter: CaseFilter & CaseTableArrangement) => Promise<CasesResponse>;
    selectedCaseType: CaseType;
    showRenewalPrices: boolean;
    editRenewalPrice: boolean;
    editSetRenewalStatus: boolean;
    renewalRegistrationActive: boolean;
    verifiedUser: ApplicationUser | null;
};

function CaseTableWrapper(props: CaseTableWrapperProps) {
    const classes = useCasePageStyles();
    const [caseTable, setCaseTable] = useState(initialCaseTable);
    const [caseTableHeaders, setCaseTableHeaders] = useState(initialCaseTableHeaders);
    const [caseTableArrangement, setCaseTableArrangement] = useState(initialCaseTableArrangement);
    const [initialTableArrangeIsComplete, setInitialTableArrangeIsComplete] = useState(false);
    const [initialLoadIsComplete, setInitialLoadIsComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCaseRef, setSelectedCaseRef] = useState<{ caseId: number; country: string } | null>(null);
    const [remarkOpen, setRemarkOpen] = useState<boolean>(false);
    const [selectedRenewalRemark, setSelectedRenewalRemark] = useState<string>();
    const [customExcelOpen, setCustomExcelOpen] = useState(false);
    const [bulkAction, setBulkAction] = useState<BulkRenewalAction>();
    const [selectedBulkCases, setSelectedBulkCases] = useState<Map<string, Case>>(new Map());
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [errorState, setErrorState] = useState<{ accessDenied: boolean; serviceUnavailable: boolean }>({ accessDenied: false, serviceUnavailable: false });
    const history = useHistory();
    const onCaseFilterChanged = (): void => {
        (props.onCaseFilterChanged
            ? props.onCaseFilterChanged(props.selectedCaseType, {
                  ...props.caseFilter,
                  ...caseTableArrangement,
              })
            : props.caseSummarization === CaseSummarization.Renewals
            ? getRenewals(props.selectedCaseType, { ...props.caseFilter, ...caseTableArrangement })
            : getCases(props.selectedCaseType, {
                  ...props.caseFilter,
                  ...caseTableArrangement,
              })
        )
            .then((res) => {
                setCaseTable({ ...caseTable, cases: res.cases, count: res.totalCount });
                setErrorState({ accessDenied: false, serviceUnavailable: false });
                setIsLoading(false);
            })
            .catch((e: HttpRequestError) => {
                setCaseTable({ ...caseTable, cases: [], count: 0 });
                setErrorState({ accessDenied: e.statusCode === 403, serviceUnavailable: e.statusCode !== 403 });
                setIsLoading(false);
            });
    };

    const onCaseSelected = (caseSelection: { caseId: number; country: string }): void => {
        setSelectedCaseRef(caseSelection);
    };

    const onRenewalRemarkSelected = (internalId: string): void => {
        setRemarkOpen(true);
        setSelectedCaseRef(null);
        setSelectedRenewalRemark(internalId);
    };

    const onCaseModalClosed = (): void => {
        setSelectedCaseRef(null);
    };

    const onTableArrangementChanged = (pageNumber: number, orderByDescending: boolean, orderBy: keyof Case = "name"): void => {
        const newTableArrangement = { ...caseTableArrangement };
        newTableArrangement.orderBy = orderBy;
        newTableArrangement.orderByDescending = orderByDescending;
        newTableArrangement.pageNumber = pageNumber;
        setCaseTableArrangement(newTableArrangement);
    };

    const resetTableArrangement = (): void =>
        onTableArrangementChanged(1, false, props.caseSummarization === CaseSummarization.Renewals ? "renewalDate" : "name");

    useEffect(() => {
        if (initialTableArrangeIsComplete && initialLoadIsComplete) resetTableArrangement();
        setSelectedBulkCases(new Map());
        // eslint-disable-next-line
    }, [props.caseFilter]);

    useEffect(() => {
        if (initialTableArrangeIsComplete) {
            onCaseFilterChanged();
            if (!initialLoadIsComplete) setInitialLoadIsComplete(true);
        }
        // eslint-disable-next-line
    }, [caseTableArrangement]);

    useEffect(() => {
        setCaseTableHeaders(
            getCaseTableHeaders(props.selectedCaseType, props.showRenewalPrices, props.renewalRegistrationActive, props.caseSummarization, props.verifiedUser)
        );
        resetTableArrangement();
        exitBulkMode();
        if (!initialTableArrangeIsComplete) setInitialTableArrangeIsComplete(true);
        // eslint-disable-next-line
    }, [props.selectedCaseType, props.caseSummarization]);

    useEffect(() => {
        const locationState = history.location.state as {
            caseId?: number;
            country?: string;
        };
        if (locationState && locationState.caseId && locationState.country) {
            onCaseSelected({
                caseId: locationState.caseId,
                country: locationState.country,
            });
        }
        // eslint-disable-next-line
    }, []);

    const onCaseUpdated = useCallback((updatedCase) => {
        setSelectedRenewalRemark(undefined);
        setSelectedCaseRef(null);
        setCaseTable((prevState) => {
            const newCases = prevState.cases.map((x) => applyUpdatedCaseToRow(x, updatedCase));
            return {
                ...prevState,
                cases: newCases,
            };
        });
    }, []);

    const canBulkEditRenewalStatus =
        props.caseSummarization === CaseSummarization.Renewals && props.renewalRegistrationActive && !userIsReadOnly(props.verifiedUser);

    const startBulkMode = (newBulkAction: BulkRenewalAction): void => {
        setBulkAction(newBulkAction);
        setSelectedBulkCases(new Map());
    };

    function exitBulkMode(): void {
        setBulkAction(undefined);
        setSelectedBulkCases(new Map());
        setBulkModalOpen(false);
    }

    const bulkRenewalSelection = useMemo<BulkRenewalSelection | undefined>(() => {
        if (bulkAction === undefined) return undefined;
        return {
            isSelected: (selectedCase) => selectedBulkCases.has(selectedCase.internalId),
            getSelectability: (selectedCase) => getBulkSelectability(selectedCase, bulkAction, props.editSetRenewalStatus, props.verifiedUser),
            onSelectedChanged: (selectedCase, selected) =>
                setSelectedBulkCases((prevState) => {
                    const newSelection = new Map(prevState);
                    if (selected) newSelection.set(selectedCase.internalId, selectedCase);
                    else newSelection.delete(selectedCase.internalId);
                    return newSelection;
                }),
        };
    }, [bulkAction, selectedBulkCases, props.editSetRenewalStatus, props.verifiedUser]);

    const selectableRowsOnPage = useMemo(
        () => (bulkRenewalSelection ? caseTable.cases.filter((x) => bulkRenewalSelection.getSelectability(x).selectable) : []),
        [bulkRenewalSelection, caseTable.cases]
    );

    const onSelectAllOnPageChanged = (selected: Case[]): void => {
        setSelectedBulkCases((prevState) => {
            const newSelection = new Map(prevState);
            selectableRowsOnPage.forEach((x) => (selected.length > 0 ? newSelection.set(x.internalId, x) : newSelection.delete(x.internalId)));
            return newSelection;
        });
    };

    const onBulkCaseDeselected = (deselectedCase: Case): void => {
        bulkRenewalSelection?.onSelectedChanged(deselectedCase, false);
        if (selectedBulkCases.size <= 1) setBulkModalOpen(false);
    };

    const onBulkCasesUpdated = (updatedCases: Case[]): void => {
        updatedCases.forEach(onCaseUpdated);
        exitBulkMode();
    };

    const showCaseModal = !!selectedCaseRef && !remarkOpen;

    const renewalModalContent = useMemo(() => {
        return remarkOpen && !selectedCaseRef ? (
            <RenewalRemarkModal
                internalId={selectedRenewalRemark}
                remarkOpen={remarkOpen}
                setRemarkOpen={setRemarkOpen}
                onCaseUpdated={onCaseUpdated}
                caseType={props.selectedCaseType}
            />
        ) : (
            <></>
        );
    }, [selectedCaseRef, onCaseUpdated, remarkOpen, selectedRenewalRemark, props.selectedCaseType]);

    const customExcelExportContent = useMemo(() => {
        return (
            customExcelOpen && <CustomExcelExport caseType={props.selectedCaseType} onClose={() => setCustomExcelOpen(false)} caseFilter={props.caseFilter} />
        );
    }, [customExcelOpen, props.selectedCaseType, props.caseFilter]);

    return (
        <>
            <div className={classes.caseTableContainer}>
                {isLoading ? (
                    <DotLegalSkeletonTable />
                ) : errorState.accessDenied ? (
                    generateNoDataDisplay()
                ) : errorState.serviceUnavailable ? (
                    generateServiceUnavailableDisplay()
                ) : (
                    <BulkRenewalContext.Provider value={bulkRenewalSelection}>
                        {bulkAction !== undefined && (
                            <BulkRenewalBanner
                                bulkAction={bulkAction}
                                selectedCount={selectedBulkCases.size}
                                onDeselectAll={() => setSelectedBulkCases(new Map())}
                            />
                        )}
                        <CaseTable
                            headers={caseTableHeaders}
                            caseSummary={props.caseSummarization}
                            onCaseSelected={onCaseSelected}
                            onRenewalRemarkSelected={onRenewalRemarkSelected}
                            onTableArrangementChanged={onTableArrangementChanged}
                            pageNumber={caseTableArrangement.pageNumber}
                            rows={caseTable.cases}
                            selectedCaseType={props.selectedCaseType}
                            totalCases={caseTable.count}
                            showRenewalPrice={props.showRenewalPrices}
                            editRenewalPrice={props.editRenewalPrice}
                            editSetRenewalStatus={props.editSetRenewalStatus}
                            renewalRegistrationActive={props.renewalRegistrationActive}
                            onRenewalPriceChange={async (selectedCase) => {
                                const newPrice = Number(selectedCase.price);
                                setCaseTable((prevState) => ({
                                    ...prevState,
                                    cases: prevState.cases.map((x) => (x.internalId === selectedCase.internalId ? { ...x, price: newPrice } : x)),
                                }));
                                await saveRenewalPrice(selectedCase);
                            }}
                            onCaseUpdated={onCaseUpdated}
                            verifiedUser={props.verifiedUser}
                            onSelectAllChanged={bulkRenewalSelection ? onSelectAllOnPageChanged : undefined}
                            headerCheckBoxState={getHeaderCheckBoxState(selectableRowsOnPage, new Set(Array.from(selectedBulkCases.keys())))}
                            headerCheckBoxDisabled={selectableRowsOnPage.length === 0}
                            trailingHeaderCell={canBulkEditRenewalStatus ? <BulkRenewalHeaderMenu onBulkActionSelected={startBulkMode} /> : undefined}
                        />
                        {(props.caseSummarization !== CaseSummarization.Dashboard || bulkAction !== undefined) && (
                            <Box display={"flex"} justifyContent={"flex-end"} alignItems={"flex-end"} gap={(theme) => theme.spacing(2)}>
                                {(props.caseSummarization === undefined || props.caseSummarization === CaseSummarization.Disputes) && (
                                    <ExcelExport name={"Export"} onClick={() => setCustomExcelOpen(true)} isLoading={false} />
                                )}
                                {!!props.caseSummarization &&
                                    props.caseSummarization !== CaseSummarization.Dashboard &&
                                    props.caseSummarization !== CaseSummarization.Disputes && (
                                        <ExcelExport name={"Export"} onClick={props.downloadExcel} isLoading={props.isLoadingExcel} />
                                    )}
                                {bulkAction !== undefined && (
                                    <>
                                        <Button color={"primary"} variant={"outlined"} onClick={exitBulkMode}>
                                            Cancel
                                        </Button>
                                        <Button
                                            color={"primary"}
                                            variant={"contained"}
                                            disabled={selectedBulkCases.size === 0}
                                            onClick={() => setBulkModalOpen(true)}
                                        >
                                            {`${bulkAction === RenewalStatus.Renew ? "Renew" : "Expire"} cases (${selectedBulkCases.size})`}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        )}
                    </BulkRenewalContext.Provider>
                )}
            </div>
            {showCaseModal && selectedCaseRef && (
                <CaseModalContent
                    caseId={selectedCaseRef.caseId}
                    country={selectedCaseRef.country}
                    caseType={props.selectedCaseType}
                    verifiedUser={props.verifiedUser}
                    onClose={onCaseModalClosed}
                />
            )}
            {renewalModalContent}
            {bulkModalOpen && bulkAction !== undefined && (
                <BulkRenewalModal
                    bulkAction={bulkAction}
                    selectedCases={Array.from(selectedBulkCases.values())}
                    caseType={props.selectedCaseType}
                    onClose={() => setBulkModalOpen(false)}
                    onCaseDeselected={onBulkCaseDeselected}
                    onCasesUpdated={onBulkCasesUpdated}
                />
            )}
            {customExcelExportContent}
        </>
    );
}

export default CaseTableWrapper;
